import { ActivityInfo } from '@/modules/drivers/types/activities'
import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getActivityIcon } from '@/modules/drivers/helpers/ActivityIconResolver'
import { DividerHorizontal } from '@/modules/drivers/shared/DividerHorizontal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { faBedFrontSolid, faClockSolid } from '@/lib/fa'
import moment from 'moment'

const formatDuration = (totalMinutes: number) => {
  const h = Math.floor(totalMinutes / 60)
  const m = Math.round(totalMinutes % 60)
  if (h === 0) return `${m}min`
  return m > 0 ? `${h}h ${m}min` : `${h}h`
}

const getDurationMinutes = (start: any, end: any) => {
  if (!start || !end) return 0
  const endMoment = moment(end)
  if (endMoment.year() > 3000) return 0
  return endMoment.diff(moment(start), 'minutes')
}

const parseDurationStringToMinutes = (
  duration: string | null | undefined,
): number => {
  if (!duration) return 0
  let days = 0
  let timePart = duration
  if (duration.includes('.')) {
    const parts = duration.split('.')
    days = parseInt(parts[0], 10) || 0
    timePart = parts[1]
  }
  const segments = timePart.split(':')
  const hours = parseInt(segments[0], 10) || 0
  const minutes = parseInt(segments[1], 10) || 0
  return days * 24 * 60 + hours * 60 + minutes
}

const ACTIVITY_TYPE_CONFIG: Record<
  number,
  { labelKey: string; order: number }
> = {
  3: { labelKey: 'activityName.driving', order: 0 },
  2: { labelKey: 'activityName.work', order: 1 },
  1: { labelKey: 'activityName.availability', order: 2 },
  0: { labelKey: 'activityName.rest', order: 3 },
  4: { labelKey: 'activityName.unknown', order: 4 },
}

const ACTIVITY_COLORS: Record<number, string> = {
  3: '#FC5959',
  2: '#F5AB46',
  1: '#F1E834',
  0: '#74C7ED',
  4: '#D2D2D2',
}

const CalendarDayInfoActivityList = ({
  activities,
}: {
  activities: ActivityInfo | []
}) => {
  const { t } = useLocalize()

  if (!activities || Array.isArray(activities) || !activities.activities) {
    return null
  }

  const MEANINGFUL_TYPES = new Set([1, 2, 3])
  const hasMeaningfulActivities = activities.activities.some((a) =>
    MEANINGFUL_TYPES.has(a.type),
  )

  if (!hasMeaningfulActivities) {
    return (
      <p className="tw-py-2 tw-text-sm tw-text-content-base/50">
        {t('driverModule.no-activities')}
      </p>
    )
  }

  const groupedActivities = activities.activities
    .filter((a) => MEANINGFUL_TYPES.has(a.type))
    .reduce(
      (acc, act) => {
        const type = act.type
        if (!acc[type]) acc[type] = { items: [], totalMinutes: 0 }
        acc[type].items.push(act)
        acc[type].totalMinutes += getDurationMinutes(act.startTime, act.endTime)
        return acc
      },
      {} as Record<number, { items: any[]; totalMinutes: number }>,
    )

  const sortedActivityGroups = Object.entries(groupedActivities)
    .map(([type, data]) => ({
      type: Number(type),
      count: data.items.length,
      totalMinutes: data.totalMinutes,
    }))
    .sort(
      (a, b) =>
        (ACTIVITY_TYPE_CONFIG[a.type]?.order ?? 99) -
        (ACTIVITY_TYPE_CONFIG[b.type]?.order ?? 99),
    )

  const restEvents: Array<{
    icon: any
    label: string
    count: number
    totalMinutes: number
    color: string
  }> = []

  if (activities.dailyRests?.length) {
    const total = activities.dailyRests.reduce(
      (sum, r) =>
        sum + parseDurationStringToMinutes((r as any).formalRestDuration),
      0,
    )
    restEvents.push({
      icon: faBedFrontSolid,
      label: t('eventShortName.dailyRest') || 'Odpoczynek dzienny',
      count: activities.dailyRests.length,
      totalMinutes: total,
      color: '#74C7ED',
    })
  }

  if (activities.dailyPartialRegularRests?.length) {
    const total = activities.dailyPartialRegularRests.reduce(
      (sum, r) =>
        sum + parseDurationStringToMinutes((r as any).formalRestDuration),
      0,
    )
    restEvents.push({
      icon: faBedFrontSolid,
      label:
        t('eventShortName.dailyPartialRegularRest') ||
        'Odpoczynek dzienny dzielony',
      count: activities.dailyPartialRegularRests.length,
      totalMinutes: total,
      color: '#E064EB',
    })
  }

  if (activities.weeklyRests?.length) {
    const total = activities.weeklyRests.reduce(
      (sum, r) =>
        sum + parseDurationStringToMinutes((r as any).formalRestDuration),
      0,
    )
    restEvents.push({
      icon: faBedFrontSolid,
      label: t('eventShortName.weeklyRest') || 'Odpoczynek tygodniowy',
      count: activities.weeklyRests.length,
      totalMinutes: total,
      color: '#5BA3C9',
    })
  }

  if (activities.weeklyReducedRests?.length) {
    const total = activities.weeklyReducedRests.reduce(
      (sum, r) =>
        sum + parseDurationStringToMinutes((r as any).formalRestDuration),
      0,
    )
    restEvents.push({
      icon: faBedFrontSolid,
      label:
        t('eventShortName.weeklyReducedRest') ||
        'Odpoczynek tygodniowy skrócony',
      count: activities.weeklyReducedRests.length,
      totalMinutes: total,
      color: '#5BA3C9',
    })
  }

  if (activities.breaks?.length) {
    const total = activities.breaks.reduce(
      (sum, r) =>
        sum + parseDurationStringToMinutes((r as any).formalRestDuration),
      0,
    )
    restEvents.push({
      icon: faClockSolid,
      label: t('eventShortName.break') || 'Przerwa',
      count: activities.breaks.length,
      totalMinutes: total,
      color: '#8BC34A',
    })
  }

  if (activities.partialBreaks?.length) {
    const total = activities.partialBreaks.reduce(
      (sum, r) =>
        sum + parseDurationStringToMinutes((r as any).formalRestDuration),
      0,
    )
    restEvents.push({
      icon: faClockSolid,
      label: t('eventShortName.partialBreak') || 'Przerwa dzielona',
      count: activities.partialBreaks.length,
      totalMinutes: total,
      color: '#8BC34A',
    })
  }

  return (
    <div className="tw-space-y-2 tw-overflow-y-auto tw-text-sm">
      {/* <p className="tw-text-xs tw-font-semibold tw-uppercase tw-text-content-base/80">
        Aktywności
      </p> */}
      {sortedActivityGroups.map(({ type, count, totalMinutes }) => (
        <ActivityLabel
          key={type}
          count={count}
          totalMinutes={totalMinutes}
          type={type}
        />
      ))}

      {restEvents.length > 0 && (
        <>
          <DividerHorizontal />
          {restEvents.map((event, idx) => (
            <ActivityLabel
              key={idx}
              count={event.count}
              totalMinutes={event.totalMinutes}
              label={event.label}
              color={event.color}
            />
          ))}
        </>
      )}
    </div>
  )
}

export default CalendarDayInfoActivityList

const ActivityLabel = ({
  type,
  count,
  totalMinutes,
  icon,
  label,
  color,
}: {
  type?: number
  count: number
  totalMinutes: number
  icon?: any
  label?: string
  color?: string
}) => {
  const { t } = useLocalize()
  const resolvedIcon =
    icon ?? (type != null ? getActivityIcon(type) : undefined)
  const resolvedColor =
    color ?? (type != null ? ACTIVITY_COLORS[type] : undefined)
  const resolvedLabel =
    label ??
    (type != null
      ? t(ACTIVITY_TYPE_CONFIG[type]?.labelKey || 'activityName.unknown')
      : '')

  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-py-0.5">
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <div
          className="tw-flex tw-h-[16px] tw-w-[32px] tw-items-center tw-justify-start tw-rounded tw-py-0.5 tw-pl-0.5"
          style={{ backgroundColor: resolvedColor }}
        >
          {resolvedIcon && (
            <FontAwesomeIcon icon={resolvedIcon} className="tw-w-3" />
          )}
        </div>
        <div className="tw-flex tw-items-center tw-gap-1">
          <span className="tw-text-sm tw-text-content-base">
            {resolvedLabel}{' '}
          </span>
          <div className="tw-grid tw-w-fit tw-place-items-center tw-bg-base-200 tw-px-1 tw-py-0.5 tw-text-xs tw-font-medium tw-text-content-base/70">
            x{count}
          </div>
        </div>
      </div>
      <span className="tw-text-sm tw-font-medium tw-text-content-base">
        {formatDuration(totalMinutes)}
      </span>
    </div>
  )
}
